import { FileText, GraduationCap, HelpCircle, MessageCircleQuestion } from 'lucide-react';
import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import ErrorDisplay from '../../../components/ErrorDisplay';
import { Modal } from '../../../components/Modal';
import { communityQuestionsService } from '../services/communityQuestionsService';
import type { CommunityQuestion, CommunityQuestionOption } from '../types/community-questions';
import { QuestionOptionsManager } from './edit-question/QuestionOptionsManager';
import QuestionMarkdownPreview from './QuestionMarkdownPreview';

interface EditQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  question?: CommunityQuestion;
  duplicateData?: CommunityQuestion;
  onSuccess: (question: CommunityQuestion, isEdit: boolean) => void;
}

interface EditableQuestionData {
  text: string;
  description: string;
  learning: string;
  type: 'question' | 'poll';
  options: Record<string, CommunityQuestionOption>;
  optionsOrder: string[];
}

export const EditQuestionModal = ({ isOpen, onClose, question, duplicateData, onSuccess }: EditQuestionModalProps) => {
  const isEditMode = !!question;

  const [formData, setFormData] = useState<EditableQuestionData>({
    text: '',
    description: '',
    learning: '',
    type: 'question',
    options: {
      '1': { label: '', responseCount: 0, isCorrect: false },
      '2': { label: '', responseCount: 0, isCorrect: false },
    },
    optionsOrder: ['1', '2'],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Initialize form data when question changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (question) {
        // Edit mode - use question data
        setFormData({
          text: question.text,
          description: question.description || '',
          learning: question.learning || '',
          type: question.type,
          options: question.options,
          optionsOrder: question.optionsOrder,
        });
      } else if (duplicateData) {
        setFormData({
          text: duplicateData.text,
          description: duplicateData.description || '',
          learning: duplicateData.learning || '',
          type: duplicateData.type,
          options: duplicateData.options,
          optionsOrder: duplicateData.optionsOrder,
        });
      } else {
        // Create new - reset to default
        setFormData({
          text: '',
          description: '',
          learning: '## ',
          type: 'question',
          options: {
            '1': { label: '', responseCount: 0, isCorrect: false },
            '2': { label: '', responseCount: 0, isCorrect: false },
            '3': { label: '', responseCount: 0, isCorrect: false },
            '4': { label: '', responseCount: 0, isCorrect: false },
          },
          optionsOrder: ['1', '2', '3', '4'],
        });
      }
      setError('');
    }
  }, [question, duplicateData, isOpen]);

  const handleBasicFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleOptionsChange = (field: string, value: Record<string, CommunityQuestionOption> | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = (): string | null => {
    if (!formData.text.trim()) {
      return 'Question text is required';
    }
    if (formData.optionsOrder.length < 2) {
      return 'At least 2 options are required';
    }

    // Check if all options have labels
    for (const optionId of formData.optionsOrder) {
      if (!formData.options[optionId]?.label?.trim()) {
        return `Option ${optionId} must have a label`;
      }
    }

    // For questions (not polls), ensure at least one option is marked as correct
    if (formData.type === 'question') {
      const hasCorrectAnswer = formData.optionsOrder.some(
        optionId => formData.options[optionId]?.isCorrect
      );
      if (!hasCorrectAnswer) {
        return 'At least one option must be marked as correct for questions';
      }
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      let updatedQuestion: CommunityQuestion;

      if (isEditMode && question) {
        updatedQuestion = await communityQuestionsService.updateQuestion(question.id, {
          text: formData.text,
          description: formData.description || undefined,
          learning: formData.learning || undefined,
          type: formData.type,
          options: formData.options,
          optionsOrder: formData.optionsOrder,
        });
      } else {
        updatedQuestion = await communityQuestionsService.createQuestion({
          text: formData.text,
          description: formData.description || undefined,
          learning: formData.learning || undefined,
          type: formData.type,
          options: formData.options,
          optionsOrder: formData.optionsOrder,
        });
      }

      onSuccess(updatedQuestion, isEditMode);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} question`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title={isEditMode ? "Edit Question" : "Create New Question"}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorDisplay message={error} />}

        {/* Question Basic Fields */}
        <div className="space-y-4">
          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type *
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => handleBasicFieldChange('type', 'question')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${formData.type === 'question'
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  <span className="font-medium">Question</span>
                </div>
                <p className="text-xs mt-1 opacity-75">Has correct answers</p>
              </button>
              <button
                type="button"
                onClick={() => handleBasicFieldChange('type', 'poll')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${formData.type === 'poll'
                  ? 'border-purple-500 bg-purple-50 text-purple-900'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <MessageCircleQuestion className="w-5 h-5" />
                  <span className="font-medium">Poll</span>
                </div>
                <p className="text-xs mt-1 opacity-75">No correct answers</p>
              </button>
            </div>
          </div>

          {/* Question Text */}
          <div>
            <label htmlFor="text" className="block text-sm font-medium text-gray-700 mb-1">
              Question Text *
            </label>
            <textarea
              id="text"
              value={formData.text}
              onChange={(e) => handleBasicFieldChange('text', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter the question text"
              rows={1}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              <FileText className="w-4 h-4" />
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleBasicFieldChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Optional description to provide more context"
              rows={1}
            />
          </div>

          <QuestionOptionsManager
            options={formData.options}
            optionsOrder={formData.optionsOrder}
            type={formData.type}
            onChange={handleOptionsChange}
          />

          {/* Learning */}
          <div>
            <label htmlFor="learning" className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              <GraduationCap className="w-4 h-4" />
              Learning
            </label>
            <textarea
              id="learning"
              value={formData.learning}
              onChange={(e) => handleBasicFieldChange('learning', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Educational content or insights related to this question"
              rows={4}
            />
          </div>
        </div>

        <QuestionMarkdownPreview markdown={formData.learning} />

        {error && <ErrorDisplay message={error} />}

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            onClick={handleCancel}
            variant="secondary"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
